import { z } from 'zod';

export const TRAINING_TYPES = [
  'onboarding',
  'skill',
  'compliance',
  'safety',
  'technical',
  'soft_skill',
] as const;
export const trainingTypeEnum = z.enum(TRAINING_TYPES);
export type TrainingType = z.infer<typeof trainingTypeEnum>;

export const TRAINING_MATERIAL_TYPES = ['video', 'document', 'link', 'quiz'] as const;
export const trainingMaterialTypeEnum = z.enum(TRAINING_MATERIAL_TYPES);
export type TrainingMaterialType = z.infer<typeof trainingMaterialTypeEnum>;

export const TRAINING_STATUSES = ['active', 'archived'] as const;
export const trainingStatusEnum = z.enum(TRAINING_STATUSES);
export type TrainingStatus = z.infer<typeof trainingStatusEnum>;

const trainingMaterialSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(200),
  url: z.string().trim().max(500).optional().or(z.literal('')),
  type: trainingMaterialTypeEnum.default('document'),
});

export type TrainingMaterialInput = z.infer<typeof trainingMaterialSchema>;

/**
 * A company L&D course — content managed by HR, with per-user enrollment
 * and completion tracking. Ported from Office_project's `Training` model.
 */
export const createTrainingSchema = z.object({
  title: z.string().trim().min(2, 'Title must be at least 2 characters').max(200),
  description: z.string().trim().max(2000).optional().or(z.literal('')),
  type: trainingTypeEnum.default('skill'),
  category: z.string().trim().max(120).optional().or(z.literal('')),
  durationHours: z.coerce.number().min(0).default(0),
  materials: z.array(trainingMaterialSchema).default([]),
  passingScore: z.coerce.number().min(0).max(100).default(0),
  isRequired: z.coerce.boolean().default(false),
  targetRoles: z.array(z.string()).default([]),
});

export type CreateTrainingInput = z.infer<typeof createTrainingSchema>;

/** Matches the source's own field allow-list on update, plus `status` (for archiving via a normal edit). */
export const updateTrainingSchema = createTrainingSchema.partial().extend({
  status: trainingStatusEnum.optional(),
});

export type UpdateTrainingInput = z.infer<typeof updateTrainingSchema>;

export const enrollUsersSchema = z.object({
  userIds: z.array(z.string()).min(1, 'Provide at least one userId'),
  dueDate: z.coerce.date().optional(),
});

export type EnrollUsersInput = z.infer<typeof enrollUsersSchema>;

export const markTrainingCompleteSchema = z.object({
  score: z.coerce.number().min(0).max(100).optional(),
  notes: z.string().trim().max(1000).optional().or(z.literal('')),
});

export type MarkTrainingCompleteInput = z.infer<typeof markTrainingCompleteSchema>;

export const trainingListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  type: trainingTypeEnum.optional(),
  status: trainingStatusEnum.default('active'),
  search: z.string().trim().max(200).optional(),
});

export type TrainingListQuery = z.infer<typeof trainingListQuerySchema>;
